/**
 * Published sites (Phase 2): an artifact published at
 * `https://<name>.<SITES_BASE_DOMAIN>` (`tabbio.site` in production).
 * See docs/planning/agent-artifacts-phase2-plan-2026-09-24.md, sections 4,
 * 5.4 and 5.5. Name rules live in `@tabbio/utils/site-names`.
 */

import type { AgentArtifactKind, PageFinding } from "./artifacts";

/**
 * `draft`: created from the composer's Sites mode before the first publish;
 * it has no name, no URL and no deploy yet.
 */
export type PublishedSiteStatus = "draft" | "live" | "unpublished" | "suspended";

export type SiteDeployStatus = "building" | "ready" | "failed";

/** Why a name cannot be claimed (`SiteNameAvailability.reason`). */
export type SiteNameUnavailableReason =
  /** Not a 3 to 40 character DNS label of letters, digits and hyphens. */
  | "invalid"
  /** Contains a blocked word, or an admin or abuse ban. */
  | "blocked"
  /** A Tabbio platform, brand or infrastructure name. */
  | "reserved"
  /** Equal to another member's username or a company slug. */
  | "username_protected"
  /** Another site has it. */
  | "taken"
  /** On hold after an unpublish, a rename or an account deletion. */
  | "held";

/** `GET /api/sites/name-availability?name=` */
export interface SiteNameAvailability {
  /** The name as sent. */
  name: string;
  /** Lowercased and trimmed, as it would be stored. */
  normalized: string;
  available: boolean;
  reason?: SiteNameUnavailableReason;
  /** Plain words in the request language (en or ar). */
  message: string;
  /** Up to 3 names, each checked and available. */
  suggestions: string[];
  /** `https://<normalized>.<baseDomain>` */
  url: string;
}

/** The deploy a site currently serves. */
export interface PublishedSiteDeployRef {
  id: string;
  number: number;
  /** The artifact version it was built from (null once that version is pruned). */
  versionNumber: number | null;
  status: SiteDeployStatus;
  createdAt: string;
}

/** A row of `GET /api/sites` and the base of the site detail. */
export interface PublishedSiteSummary {
  id: string;
  artifactId: string;
  /** Null only for a draft, which has not chosen its address yet. */
  name: string | null;
  /** `https://<name>.<baseDomain>`; null when `name` is. */
  url: string | null;
  status: PublishedSiteStatus;
  /** The chat thread the site is edited in (null once that thread is deleted). */
  threadId: string | null;
  /**
   * The thumbnail as a 15-minute signed URL minted for this response, never
   * the storage key; do not persist it. Null when there is no thumbnail yet.
   */
  previewUrl: string | null;
  indexable: boolean;
  showBadge: boolean;
  title: string;
  kind: AgentArtifactKind;
  currentDeploy: PublishedSiteDeployRef | null;
  latestVersionNumber: number;
  /** The artifact's head is newer than the deployed version. */
  hasUnpublishedChanges: boolean;
  /** Views in the last 7 UTC days (materialized daily). */
  views7d: number;
  views30d: number;
  publishedAt: string;
  updatedAt: string;
}

/** `GET /api/sites/:siteId`, `GET /api/artifacts/:id/site` and publish answers. */
export interface PublishedSiteDetail extends PublishedSiteSummary {
  /** The address before the last rename, redirecting until `previousNameUntil`. */
  previousName: string | null;
  previousNameUntil: string | null;
  /** Findings that block publishing the head version. */
  checks: { errors: PageFinding[] };
  suspension: { reason: string } | null;
}

export interface PublishedSiteList {
  sites: PublishedSiteSummary[];
  nextCursor: string | null;
}

/**
 * The two tabs of the Sites list (plan 7.10). `shared` lists sites other
 * members shared with the caller; it is empty until sharing exists.
 */
export type SiteListTab = "owned" | "shared";

/** `GET /api/sites?tab=&q=&status=&cursor=&limit=` */
export interface SiteListQuery {
  /** Default `owned`. */
  tab?: SiteListTab;
  /** Filters by title and name as the user types (case-insensitive). */
  q?: string;
  status?: PublishedSiteStatus;
  cursor?: string;
  /** 1 to 50, default 20. */
  limit?: number;
}

/**
 * `POST /api/sites/drafts`: a draft site bound to a page artifact and its
 * chat thread, created from the composer's Sites mode (plan 7.10). The
 * artifact must exist (its first page); publishing it later turns the draft
 * live under the chosen name.
 */
export interface CreateSiteDraftRequest {
  artifactId: string;
  /** Defaults to the artifact's own thread. */
  threadId?: string;
}

/**
 * `POST /api/sites/:siteId/delete`: removes the site for good (the address
 * stops working at once and stays held for its owner). With `keepArtifact`
 * false the page is deleted from the user's files too.
 */
export interface DeleteSiteRequest {
  keepArtifact: boolean;
}

/**
 * What `publishArtifact` returns in chat: it never publishes there. The
 * `site-publish` card shows the address and opens the Publish sheet
 * prefilled, so a person always confirms the name and the plan terms.
 */
export interface SitePublishSheetToolResult {
  action: "open_publish_sheet";
  artifactId: string;
  /** An available name (the one asked for when free), or null when none was found. */
  suggestedName: string | null;
  /** `https://<suggestedName>.<baseDomain>`, or null with no name. */
  url: string | null;
  /** The artifact already has a site (live, unpublished or draft). */
  site: PublishedSiteSummary | null;
}

/**
 * What `unpublishArtifact` returns, and `publishArtifact` once an owner
 * approved an MCP call (card type `site-publish`).
 */
export interface SiteChangedToolResult {
  action: "published" | "unpublished";
  artifactId: string;
  siteId: string;
  site: PublishedSiteSummary;
}

/** A row of `GET /api/sites/:siteId/deploys` (site history). */
export interface SiteDeploySummary {
  id: string;
  number: number;
  versionNumber: number | null;
  status: SiteDeployStatus;
  createdAt: string;
  byteLength: number;
}

export interface SiteDeployList {
  deploys: SiteDeploySummary[];
}

/** One UTC day of `GET /api/sites/:siteId/stats`. */
export interface SiteDailyStats {
  /** `YYYY-MM-DD` (UTC). */
  day: string;
  views: number;
  /** Approximate; null when visitor counting is off. */
  visitors: number | null;
}

export interface SiteStats {
  /** One entry per UTC day, oldest first, zero filled, ending today. */
  days: SiteDailyStats[];
  /** Over `days`. */
  totals: { views: number; visitors: number | null };
  /*
   * The fields below are always sent by the API since WP11 (optional here so
   * older fixtures still type-check). They do not depend on `days`.
   */
  /** Every day and window is a UTC day (views are bucketed by UTC day when counted). */
  timeZone?: "UTC";
  /** Views in the last 7 and 30 UTC days, today included. */
  views7d?: number;
  views30d?: number;
  /**
   * The sum of each day's approximate unique visitors over the same windows
   * (a visitor seen on two days counts twice); null when visitor counting
   * is off.
   */
  visitors7d?: number | null;
  visitors30d?: number | null;
  /** Every counted view since the site was first published. */
  viewsAllTime?: number;
  /** ISO time of the last counted view (flushed every 10 s); null before the first. */
  lastViewedAt?: string | null;
}

/** `POST /api/artifacts/:id/publish` */
export interface PublishSiteRequest {
  name: string;
  /** Omitted means the head version. */
  versionId?: string;
  /** Default false: search engines are asked not to index the site. */
  indexable?: boolean;
  /** Paid plans only may send false. */
  showBadge?: boolean;
}

/** `PATCH /api/sites/:siteId` (a rename rebuilds the deploy). */
export interface UpdateSiteRequest {
  name?: string;
  /** The site's own title, as the Sites list shows it (no rebuild). */
  title?: string;
  indexable?: boolean;
  showBadge?: boolean;
}

/** `POST /api/sites/:siteId/deploys` (Update site). */
export interface SiteDeployRequest {
  /** Omitted means the head version. */
  versionId?: string;
}

/** `POST /api/sites/:siteId/rollback` */
export interface SiteRollbackRequest {
  deployId: string;
}

/**
 * Socket nudge (`SOCKET_EVENTS.SITE`) sent to the owner when a site or its
 * deploy changes. Clients refetch the site.
 */
export interface SiteSocketEvent {
  siteId: string;
  artifactId: string;
  status: PublishedSiteStatus;
  deploy: { id: string; number: number; status: SiteDeployStatus } | null;
}

/** Reasons offered by the public report form. */
export type SiteReportReason =
  | "phishing"
  | "malware"
  | "spam"
  | "impersonation"
  | "illegal"
  | "copyright"
  | "other";

export type SiteReportStatus = "open" | "actioned" | "dismissed";

/**
 * `site_name_reservation.reason`. `admin` and `abuse` block the name for
 * everyone; `held` keeps it for `userId` (or for nobody); `allow` keeps it
 * for `userId` and lets that owner's site publish past the phishing
 * heuristics (an admin override, plan 9.3).
 */
export type SiteNameReservationReason = "admin" | "abuse" | "held" | "allow";

/**
 * Why a site was suspended, as the owner is told (`suspension.reason`).
 * `unsafe_links` is a Web Risk match; the rest follow the policy's sections.
 */
export const SITE_SUSPENSION_REASONS = [
  "phishing",
  "malware",
  "unsafe_links",
  "impersonation",
  "spam",
  "illegal",
  "hate",
  "child_safety",
  "copyright",
  "other",
] as const;

export type SiteSuspensionReason = (typeof SITE_SUSPENSION_REASONS)[number];

/** What suspended a site automatically (plan 9.3 and 9.5). */
export type SiteSuspensionRule = "reports" | "web_risk" | "phishing";

/** `published_site.suspension` as stored (never sent to the owner whole). */
export interface PublishedSiteSuspension {
  reason: SiteSuspensionReason | string;
  /** A legal takedown: the host answers 451 instead of 410. */
  legal: boolean;
  reportIds: string[];
  /** The admin's user id, or `system` for an automatic suspension. */
  actorId: string;
  note?: string | null;
  source?: "admin" | "auto";
  rule?: SiteSuspensionRule;
  /** The links Web Risk matched, or the heuristics that fired. */
  evidence?: string[];
}

/**
 * The acceptable-use policy for published sites, linked from the sites
 * service's pages and the publish sheet.
 */
export const SITES_POLICY_URL = "https://www.tabbio.com/en/sites-policy";
export const SITES_POLICY_URL_AR = "https://www.tabbio.com/ar/sites-policy";
/** Existing support inbox for published-site reports and appeals. */
export const SITES_ABUSE_EMAIL = "support@tabbio.com";
/**
 * The reserved host label that serves the report form,
 * `https://report.tabbio.site/report` (the bare domain forwards to
 * www.tabbio.com at the registrar, so the apex never reaches the service).
 */
export const SITES_REPORT_LABEL = "report";

/**
 * Cache class of a deployed file: `html` and `revalidate` answer
 * `max-age=0, must-revalidate` with an ETag; `immutable` is content-addressed
 * (`/_assets/*`, `/fonts/*`).
 */
export type SiteDeployFileCache = "html" | "immutable" | "revalidate";

/** One file of a deploy (`site_deploy.manifest.files`). */
export interface SiteDeployManifestFile {
  /** URL path on the site, for example `/`, `/document.pdf` or `/_assets/<sha>.webp`. */
  path: string;
  /** Object key in the sites bucket. */
  key: string;
  contentType: string;
  bytes: number;
  /** Hex sha256 of the body. */
  sha256: string;
  cache: SiteDeployFileCache;
  disposition?: "inline" | "attachment";
}

/** `site_deploy.manifest`: the only files a deploy serves. */
export interface SiteDeployManifest {
  files: SiteDeployManifestFile[];
  etag: string;
  warnings: string[];
  /**
   * The published document's language (for example `ar`): the sites
   * service shows the site's own 404 page in it.
   */
  lang?: string;
}

export type SiteErrorCode =
  /** 403: publishing is off, or the user is outside the canary. */
  | "SITES_DISABLED"
  /** 404: unknown, foreign or deleted site. */
  | "SITE_NOT_FOUND"
  /** 422: the name breaks the format rules. */
  | "SITE_NAME_INVALID"
  /** 409: `details.reason` and `details.suggestions`. */
  | "SITE_NAME_UNAVAILABLE"
  /** 422: the version has blocking findings; `details.findings`. */
  | "SITE_CHECKS_FAILED"
  /** 422: links failed the safety check; `details.urls`. */
  | "SITE_LINK_BLOCKED"
  /** 422: the deploy is larger than the plan allows. */
  | "SITE_TOO_LARGE"
  /** 500 on the deploy, surfaced as the deploy status. */
  | "SITE_DEPLOY_FAILED"
  /** 403: a suspended site cannot be republished or renamed by its owner. */
  | "SITE_SUSPENDED"
  /** 409: update, rename and rollback need a live site. */
  | "SITE_NOT_LIVE"
  /** 404 from the API for a request whose Host is the sites domain. */
  | "SITE_HOST_NOT_SERVED_HERE";
